import {
  createV2App,
  createV2DebugProfile,
  createV2Flow,
  studioV2NewAppDraft,
  studioV2NewFlowDraft,
  updateV2Flow,
} from '../../api/studioV2.api';
import { createFormDesign } from '../../api/formBuilder.api';
import { formElementsToStudioV2AppFields } from '../../utils/formDesignToStudioV2Fields';
import { normalizeV2FlowEdge } from './studioV2FlowModel';
import { STUDIO_V2_APP_TEMPLATES } from './studioV2AppTemplates';
import { STUDIO_V2_FLOW_TEMPLATES } from './studioV2FlowTemplates';
import { STUDIO_V2_FORM_TEMPLATES } from './studioV2FormTemplates';

/** Build a persisted flow definition from a catalog entry (new id, normalized edges). */
export function buildFlowDefinitionForCreate(flowTemplateEntry) {
  const def = flowTemplateEntry.definition;
  const draft = studioV2NewFlowDraft(def?.name || 'Untitled flow');
  let merged = {
    ...def,
    id: draft.id,
    schemaVersion: 'flow/v2',
  };
  if (Array.isArray(merged.edges)) {
    merged = { ...merged, edges: merged.edges.map((edge) => normalizeV2FlowEdge(edge)) };
  }
  return merged;
}

/** Build a persisted app definition from a catalog entry (new id). */
export function buildAppDefinitionForCreate(appTemplateEntry) {
  const shell = studioV2NewAppDraft(appTemplateEntry.definition?.name || 'Untitled app');
  return {
    ...appTemplateEntry.definition,
    id: shell.id,
    name: appTemplateEntry.definition?.name || shell.name,
    schemaVersion: 'app/v2',
  };
}

export async function createFlowFromTemplateEntry(flowTemplateEntry) {
  const body = buildFlowDefinitionForCreate(flowTemplateEntry);
  return createV2Flow(body);
}

export async function createAppFromTemplateEntry(appTemplateEntry) {
  const body = buildAppDefinitionForCreate(appTemplateEntry);
  return createV2App(body);
}

export function buildFormDefinitionForCreate(formTemplateEntry) {
  const definition = formTemplateEntry.definition || {};
  return {
    name: definition.name || formTemplateEntry.label || 'Untitled form',
    description: definition.description || formTemplateEntry.description || '',
    formElements: definition.formElements || [],
    formSettings: definition.formSettings || {},
    payloadContract: definition.payloadContract,
    debugPayload: definition.debugPayload,
    formConfig: {
      version: '1.0.0',
      createdAt: new Date().toISOString(),
    },
    status: 'draft',
    tags: formTemplateEntry.tags || [],
    category: formTemplateEntry.category || 'general',
  };
}

export async function createFormFromTemplateEntry(formTemplateEntry) {
  const body = buildFormDefinitionForCreate(formTemplateEntry);
  return createFormDesign(body);
}

function attachFormPanelToApp(appBody, {
  formTemplate,
  formMongoId,
  flowMongoId,
  panelId = 'intake_form',
  interviewPhase,
  position = 'right',
  order = 99,
}) {
  if (!formTemplate?.definition || !formMongoId) return appBody;
  const fields = formElementsToStudioV2AppFields(formTemplate.definition.formElements || []);
  const contract = formTemplate.definition.payloadContract;
  const intakePanel = {
    id: panelId,
    type: 'component',
    componentType: 'form',
    position,
    order,
    outputKey: 'form_prefill',
    source: {
      flowRef: flowMongoId ? String(flowMongoId) : undefined,
      outputPath: 'result.form_prefill',
    },
    config: {
      formDesignId: String(formMongoId),
      formFields: fields,
      inputMapping: contract?.inputMapping || [],
      payloadContract: contract,
      formTitle: formTemplate.definition.formSettings?.title || formTemplate.definition.name,
      submitLabel: formTemplate.definition.formSettings?.submitLabel || 'Submit',
      ...(interviewPhase ? { interviewPhase } : {}),
    },
  };
  const panels = Array.isArray(appBody.panels) ? [...appBody.panels] : [];
  if (!panels.some((p) => p.id === panelId)) {
    panels.push(intakePanel);
  }
  return { ...appBody, panels };
}

function wireAppPanelsToFlow(appBody, flowMongoId, { phaseId } = {}) {
  const flowRef = String(flowMongoId);
  return {
    ...appBody,
    panels: (Array.isArray(appBody.panels) ? appBody.panels : []).map((panel) => {
      if (panel?.type !== 'component') return panel;
      if (phaseId && panel?.config?.interviewPhase && panel.config.interviewPhase !== phaseId) return panel;
      return {
        ...panel,
        source: {
          ...(panel.source || {}),
          flowRef,
        },
      };
    }),
  };
}

export async function createRecruitingPilotBundle(bundleEntry) {
  const screeningFormTpl = STUDIO_V2_FORM_TEMPLATES.find((t) => t.id === 'candidate-screening-intake');
  const scorecardFormTpl = STUDIO_V2_FORM_TEMPLATES.find((t) => t.id === 'interview-feedback');
  const screeningFlowTpl = STUDIO_V2_FLOW_TEMPLATES.find((t) => t.id === 'candidate-screening');
  const scorecardFlowTpl = STUDIO_V2_FLOW_TEMPLATES.find((t) => t.id === 'interview-scorecard');
  const appTemplate = STUDIO_V2_APP_TEMPLATES.find((t) => t.id === 'recruiting-pilot-console');
  const result = {};

  if (screeningFormTpl) result.screeningForm = await createFormFromTemplateEntry(screeningFormTpl);
  if (scorecardFormTpl) result.scorecardForm = await createFormFromTemplateEntry(scorecardFormTpl);
  if (screeningFlowTpl) result.screeningFlow = await createFlowFromTemplateEntry(screeningFlowTpl);
  if (scorecardFlowTpl) result.scorecardFlow = await createFlowFromTemplateEntry(scorecardFlowTpl);

  const screeningFormId = result.screeningForm?.form?._id || result.screeningForm?._id || null;
  const scorecardFormId = result.scorecardForm?.form?._id || result.scorecardForm?._id || null;
  const screeningFlowId = result.screeningFlow?.flow?._id || result.screeningFlow?.flow?.id || null;
  const scorecardFlowId = result.scorecardFlow?.flow?._id || result.scorecardFlow?.flow?.id || null;
  const screeningFlowDef = result.screeningFlow?.flow?.definition;
  const scorecardFlowDef = result.scorecardFlow?.flow?.definition;

  if (screeningFlowId && screeningFormTpl) {
    await linkFormToFlow(screeningFlowId, screeningFlowDef, screeningFormTpl, screeningFormId);
    await seedDebugProfiles(screeningFlowId, screeningFormTpl, `${bundleEntry.label || 'Recruiting pilot'} — screening`);
  }
  if (scorecardFlowId && scorecardFormTpl) {
    await linkFormToFlow(scorecardFlowId, scorecardFlowDef, scorecardFormTpl, scorecardFormId);
    await seedDebugProfiles(scorecardFlowId, scorecardFormTpl, `${bundleEntry.label || 'Recruiting pilot'} — scorecard`);
  }

  if (appTemplate && screeningFlowId && scorecardFlowId) {
    let appBody = buildAppDefinitionForCreate(appTemplate);
    appBody.defaultFlowId = String(screeningFlowId);
    appBody = wireAppPanelsToFlow(appBody, screeningFlowId, { phaseId: 'screening' });
    appBody = wireAppPanelsToFlow(appBody, scorecardFlowId, { phaseId: 'scorecard' });
    appBody.metadata = appBody.metadata && typeof appBody.metadata === 'object' ? appBody.metadata : {};
    appBody.metadata.interviewFlow = {
      ...(appBody.metadata.interviewFlow || {}),
      defaultChatFlowRef: String(screeningFlowId),
      phaseFlows: {
        screening: String(screeningFlowId),
        scorecard: String(scorecardFlowId),
      },
      bundleId: bundleEntry.id,
      ...(bundleEntry.metadata || {}),
    };
    if (screeningFormTpl && screeningFormId) {
      appBody = attachFormPanelToApp(appBody, {
        formTemplate: screeningFormTpl,
        formMongoId: screeningFormId,
        flowMongoId: screeningFlowId,
        panelId: 'screening_intake',
        interviewPhase: 'screening',
        order: 98,
      });
    }
    if (scorecardFormTpl && scorecardFormId) {
      appBody = attachFormPanelToApp(appBody, {
        formTemplate: scorecardFormTpl,
        formMongoId: scorecardFormId,
        flowMongoId: scorecardFlowId,
        panelId: 'scorecard_intake',
        interviewPhase: 'scorecard',
        order: 99,
      });
    }
    result.app = await createV2App(appBody);
  }

  const error = result.screeningForm?.error
    || result.scorecardForm?.error
    || result.screeningFlow?.error
    || result.scorecardFlow?.error
    || result.app?.error
    || null;
  return { ...result, error };
}

async function linkFormToFlow(flowMongoId, flowDefinition, formTemplate, formMongoId) {
  if (!flowMongoId || !flowDefinition) return;
  const debugInput = formTemplate?.definition?.debugPayload?.input;
  const nextDef = {
    ...flowDefinition,
    metadata: {
      ...(flowDefinition.metadata || {}),
      interviewFlow: {
        ...(flowDefinition.metadata?.interviewFlow || {}),
        formDesignId: formMongoId ? String(formMongoId) : undefined,
        bundleFormTemplateId: formTemplate?.id,
      },
    },
    state: {
      ...(flowDefinition.state || {}),
      input: {
        ...(flowDefinition.state?.input || {}),
        ...(debugInput && typeof debugInput === 'object' ? debugInput : {}),
      },
    },
  };
  await updateV2Flow(String(flowMongoId), nextDef);
}

async function seedDebugProfiles(flowMongoId, formTemplate, bundleLabel) {
  if (!flowMongoId || !formTemplate?.definition?.debugPayload) return;
  const payload = formTemplate.definition.debugPayload;
    await createV2DebugProfile(`${bundleLabel} — form default input`, {
      flowId: String(flowMongoId),
      input: payload.input && typeof payload.input === 'object' ? payload.input : payload,
    });
}

export async function createUseCaseBundle(bundleEntry) {
  if (bundleEntry.multiFlow === 'recruiting-pilot' || bundleEntry.id === 'recruiting-pilot') {
    return createRecruitingPilotBundle(bundleEntry);
  }
  const formTemplate = STUDIO_V2_FORM_TEMPLATES.find((tpl) => tpl.id === bundleEntry.formTemplateId);
  const flowTemplate = STUDIO_V2_FLOW_TEMPLATES.find((tpl) => tpl.id === bundleEntry.flowTemplateId);
  const appTemplate = STUDIO_V2_APP_TEMPLATES.find((tpl) => tpl.id === bundleEntry.appTemplateId);
  const result = {};
  if (formTemplate) result.form = await createFormFromTemplateEntry(formTemplate);
  if (flowTemplate) result.flow = await createFlowFromTemplateEntry(flowTemplate);

  const formMongoId = result.form?.form?._id || result.form?._id || null;
  const createdFlowId = result.flow?.flow?._id || result.flow?.flow?.id || null;
  const flowDefinition = result.flow?.flow?.definition;

  if (createdFlowId && formTemplate) {
    await linkFormToFlow(createdFlowId, flowDefinition, formTemplate, formMongoId);
    await seedDebugProfiles(createdFlowId, formTemplate, bundleEntry.label || bundleEntry.id);
  }

  if (appTemplate) {
    let appBody = buildAppDefinitionForCreate(appTemplate);
    if (createdFlowId) {
      appBody.defaultFlowId = String(createdFlowId);
      appBody.panels = (Array.isArray(appBody.panels) ? appBody.panels : []).map((panel) => (
        panel?.type === 'component'
          ? {
            ...panel,
            source: {
              ...(panel.source || {}),
              flowRef: String(createdFlowId),
            },
          }
          : panel
      ));
      appBody.metadata = appBody.metadata && typeof appBody.metadata === 'object' ? appBody.metadata : {};
      appBody.metadata.interviewFlow = {
        ...(appBody.metadata.interviewFlow || {}),
        defaultChatFlowRef: String(createdFlowId),
        formDesignId: formMongoId ? String(formMongoId) : undefined,
        bundleId: bundleEntry.id,
        ...(bundleEntry.metadata || {}),
      };
    }
    if (bundleEntry.attachIntakeFormPanel !== false && formTemplate && formMongoId) {
      appBody = attachFormPanelToApp(appBody, {
        formTemplate,
        formMongoId,
        flowMongoId: createdFlowId,
      });
    }
    result.app = await createV2App(appBody);
    const appMongoId = result.app?.app?._id || result.app?._id;
    if (appMongoId && bundleEntry.id === 'candidate-screening' && appBody.metadata?.interviewFlow?.handoff) {
      appBody.metadata.interviewFlow.handoff = {
        ...appBody.metadata.interviewFlow.handoff,
        note: 'Click a pass row to prefill the scorecard app (when companion app is linked).',
      };
    }
  }

  const error = result.form?.error || result.flow?.error || result.app?.error || null;
  return { ...result, error };
}
